/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListShadowMCPInventoryUsersRequest,
  ListShadowMCPInventoryUsersSecurity,
} from "../models/operations/listshadowmcpinventoryusers.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildShadowMCPInventoryUsersQuery,
  prefetchShadowMCPInventoryUsers,
  queryKeyShadowMCPInventoryUsers,
  ShadowMCPInventoryUsersQueryData,
} from "./shadowMCPInventoryUsers.core.js";
export {
  buildShadowMCPInventoryUsersQuery,
  prefetchShadowMCPInventoryUsers,
  queryKeyShadowMCPInventoryUsers,
  type ShadowMCPInventoryUsersQueryData,
};

export type ShadowMCPInventoryUsersQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listShadowMCPInventoryUsers access
 *
 * @remarks
 * List users with observed telemetry usage for one project-scoped Shadow MCP server URL.
 */
export function useShadowMCPInventoryUsers(
  request: ListShadowMCPInventoryUsersRequest,
  security?: ListShadowMCPInventoryUsersSecurity | undefined,
  options?: QueryHookOptions<
    ShadowMCPInventoryUsersQueryData,
    ShadowMCPInventoryUsersQueryError
  >,
): UseQueryResult<
  ShadowMCPInventoryUsersQueryData,
  ShadowMCPInventoryUsersQueryError
> {
  const client = useGramContext();
  return useQuery({
    ...buildShadowMCPInventoryUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listShadowMCPInventoryUsers access
 *
 * @remarks
 * List users with observed telemetry usage for one project-scoped Shadow MCP server URL.
 */
export function useShadowMCPInventoryUsersSuspense(
  request: ListShadowMCPInventoryUsersRequest,
  security?: ListShadowMCPInventoryUsersSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ShadowMCPInventoryUsersQueryData,
    ShadowMCPInventoryUsersQueryError
  >,
): UseSuspenseQueryResult<
  ShadowMCPInventoryUsersQueryData,
  ShadowMCPInventoryUsersQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildShadowMCPInventoryUsersQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setShadowMCPInventoryUsersData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      projectId: string;
      serverUrl: string;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ShadowMCPInventoryUsersQueryData,
): ShadowMCPInventoryUsersQueryData | undefined {
  const key = queryKeyShadowMCPInventoryUsers(...queryKeyBase);

  return client.setQueryData<ShadowMCPInventoryUsersQueryData>(key, data);
}

export function invalidateShadowMCPInventoryUsers(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      projectId: string;
      serverUrl: string;
      limit?: number | undefined;
      cursor?: string | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "access",
      "listShadowMCPInventoryUsers",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllShadowMCPInventoryUsers(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "access", "listShadowMCPInventoryUsers"],
  });
}
